import { useEffect, useRef, useState } from "react";
import { Loader2, SearchIcon } from "lucide-react";

import { Input } from "@ui/input";
import BasicPopover from "@components/items/display/Popover";
import { translate } from "@modules/localizations/translate";
import { useDebounce } from "@utils/hooks/useDebounce";
import { useInfiniteScroll } from "@utils/hooks/useInfiniteScroll";
import { useSearchTracked } from "@lib/stores/content/hooks";
import {
  clearSearchTracked,
  searchTracked,
  setAlert,
  useAppSelector,
} from "@lib/stores";
import { Skeleton } from "@ui/skeleton";
import TranslatedText from "@components/items/display/TranslatedText";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { db } from "@modules/local/db";
import { useAppLiveQuery } from "@modules/local/hooks";
import { HorizontalListItemParameters } from "./HorizontalList";
import { ComponentsActions } from "@lib/fetchers/components";
import { getComponentParameterValues } from "@modules/content/helpers/content";
import { setStorage } from "@modules/storage/storageHelper";
import { useContentSelector } from "@modules/content/hooks/useContentSelector";
import { detailsPageType } from "@utils/helpers/detailsPageType";
import { useLockedContents } from "@modules/content/hooks/useLockedContent";
import { AlertType } from "@lib/stores/common/types";
import { SelectedPlayerContentSource } from "@lib/stores/content/types";

interface SearchPopoverProps {
  isB2B: boolean | undefined;
}

export const SearchPopover = ({ isB2B }: SearchPopoverProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const navigate = useAppNavigate();

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>();
  const [pageNumber, setPageNumber] = useState(1);

  const userId = useAppSelector((state) => state.auth.user.id ?? "");

  const { result: flow } = useAppLiveQuery(
    () => db.readerFlows.get(userId),
    [userId]
  );

  const { result: searchComponent } = useAppLiveQuery(
    () => db.components.get(flow?.searchAllComponentId || ""),
    [flow?.searchAllComponentId]
  );

  const { data, status, meta } = useSearchTracked();

  const { onSelectContent } = useContentSelector();

  const [setTermDebounced] = useDebounce((term) => {
    setIsPopoverOpen(true);

    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    clearSearchTracked();

    setPageNumber(1);
    setSearchTerm(term as string);
  });

  const { lastElementRef } = useInfiniteScroll(meta.hasNextPage, setPageNumber);

  const { isLockedContent } = useLockedContents(data || []);
  const lockedContents = isLockedContent.result;

  const clear = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }

    clearSearchTracked();

    setSearchTerm(undefined);
  };

  useEffect(() => {
    const listener = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (
        wrapperRef.current &&
        popoverRef.current &&
        !(
          wrapperRef.current.contains(target) ||
          popoverRef.current.contains(target)
        )
      ) {
        setIsPopoverOpen(false);
      }
    };

    document.body.addEventListener("click", listener);

    return () => {
      document.body.removeEventListener("click", listener);
    };
  }, []);

  useEffect(() => {
    if (searchTerm) {
      searchTracked({
        searchText: searchTerm,
        pageNumber,
        pageSize: 10,
        typeName: isB2B ? "SKILL" : undefined,
      });
    }
  }, [searchTerm, pageNumber, isB2B]);

  useEffect(() => {
    clear();
  }, [isB2B]);

  const getComponentAsync = async (id: string | undefined) => {
    if (!id) return;

    await ComponentsActions.getComponentByIdAsync(id);
  };

  useEffect(() => {
    getComponentAsync(flow?.searchAllComponentId);
  }, [flow?.searchAllComponentId]);

  const renderResult = () => {
    return getComponentParameterValues<HorizontalListItemParameters>(
      ["image"],
      searchComponent,
      data
    ).map((d, i) => {
      const isLocked = lockedContents?.find(
        (l) => l.id === d.content.id
      )?.isLocked;

      return (
        <div
          key={d.content.id}
          ref={data.length - 1 === i ? lastElementRef : null}
          className="flex cursor-pointer items-center justify-start gap-x-2"
          onClick={() => {
            onSelectContent(d.content, d.values, {
              isLocked,
              source: SelectedPlayerContentSource.Search,
            });

            // This is to clear the state when SearchPopover component
            // doesn't get unmounted and mounted back
            clear();
          }}
        >
          <div className="relative h-12 w-12 min-w-12 overflow-hidden rounded-sm">
            <img
              src={d.values.image || ""}
              alt={d.content.title}
              className="h-full w-full"
            />
            {isLocked ? (
              <div className="absolute bottom-1 left-1 flex h-5 w-5 items-center justify-center rounded-full bg-[linear-gradient(180deg,_rgba(255,_204,_101,_0.90)_0%,_rgba(255,_191,_62,_0.95)_100%)] p-1">
                <img
                  src="/assets/icons/premium.svg"
                  alt="premium"
                  className=""
                />
              </div>
            ) : null}
          </div>
          <div className="text-[14px]">{d.content.title}</div>
        </div>
      );
    });
  };

  return (
    <div ref={wrapperRef}>
      <BasicPopover
        ref={popoverRef}
        trigger={
          <div className="relative h-[41px] w-[400px]">
            <Input
              ref={inputRef}
              className="placeholder:text[16px] w-full rounded-full border-none bg-[#E7E7E7] px-10 text-black focus-visible:ring-0 focus-visible:ring-offset-0 dark:bg-[#898A8D70] dark:text-white"
              placeholder={
                isB2B !== undefined
                  ? translate(
                      !isB2B
                        ? `layout.b2c-search-placeholder`
                        : `layout.b2b-search-placeholder`
                    )
                  : ""
              }
              onChange={(event) => {
                const value = event.target.value;

                setTermDebounced(value);

                if (!value) {
                  setIsPopoverOpen(false);

                  clearSearchTracked();

                  setSearchTerm(undefined);
                }
              }}
              onFocus={(event) => {
                if (event.target.value && !isPopoverOpen) {
                  setIsPopoverOpen(true);
                }
              }}
            />
            <div className="absolute top-[20%] px-3">
              <SearchIcon className="text-[#cfcfcf]" />
            </div>
          </div>
        }
        content={
          <div className="cus-scrollbar-w-none h-[400px] w-full overflow-y-auto text-black dark:text-white">
            {data.length ? (
              <div className="flex h-full w-full flex-col gap-y-2">
                {renderResult()}
                {status === "loading" ? (
                  <div className="flex h-12 w-full items-center justify-center">
                    <Loader2 className={`animate-spin`} />
                  </div>
                ) : null}
              </div>
            ) : status === "loading" ? (
              <SkeletonLoading />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <TranslatedText text="layout.search no result" />
              </div>
            )}
          </div>
        }
        open={isPopoverOpen}
      />
    </div>
  );
};

const SkeletonLoading = () => {
  const skeletonData: { id: string }[] = Array.from({ length: 10 }).map(
    (s, i) => {
      return {
        id: i + "",
      };
    }
  );

  const renderItems = () => {
    return skeletonData.map((s) => {
      return (
        <div key={s.id} className="flex items-center justify-start gap-x-2">
          <Skeleton
            className={`h-12 w-12 min-w-12 rounded-sm bg-[#f4f4f5] dark:bg-[#27272a]`}
          ></Skeleton>
          <Skeleton
            className={`h-5 w-full rounded-sm bg-[#f4f4f5] dark:bg-[#27272a]`}
          ></Skeleton>
        </div>
      );
    });
  };

  return <div className="flex flex-col gap-y-2">{renderItems()}</div>;
};
